import { forwardRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { AnimatePresence, motion } from "motion/react";
import { CheckIcon, CopyIcon, XIcon } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useCopyToClipboard, type CopyState } from "@/hooks/useCopyToClipboard";
import { cn } from "@/utils/cn";

// ── Variants ───────────────────────────────────────────────────────────────────

const copyButtonVariants = cva(
  "inline-flex items-center shrink-0 rounded outline-none select-none transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-primary/40 disabled:cursor-default disabled:pointer-events-none",
  {
    variants: {
      size: {
        xs: "p-0.5 gap-1",
        sm: "px-1.5 py-1 gap-1.5",
        md: "px-2 py-1.5 gap-2",
      },
      copyState: {
        idle: "text-muted-foreground hover:text-foreground hover:bg-muted",
        copied: "text-success",
        error: "text-danger",
      },
    },
    defaultVariants: { size: "xs", copyState: "idle" },
  },
);

// ── Constants ──────────────────────────────────────────────────────────────────

const ICON_SIZE = { xs: "size-3", sm: "size-3.5", md: "size-4" } as const;
const TEXT_SIZE = { xs: "text-[10px]", sm: "text-xs", md: "text-sm" } as const;

const buildStateMeta = (
  t: TFunction,
): Record<
  CopyState,
  { Icon: React.ElementType; tooltip: string; label: string }
> => ({
  idle: {
    Icon: CopyIcon,
    tooltip: t("common:buttons.copy"),
    label: t("common:buttons.copy"),
  },
  copied: {
    Icon: CheckIcon,
    tooltip: t("common:buttons.copied"),
    label: t("common:buttons.copied"),
  },
  error: {
    Icon: XIcon,
    tooltip: t("common:buttons.failed_to_copy"),
    label: t("common:buttons.error_label"),
  },
});

// ── Props ──────────────────────────────────────────────────────────────────────

export interface CopyToClipboardProps
  extends
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onClick">,
    VariantProps<typeof copyButtonVariants> {
  value: string;
  label?: string;
  showLabel?: boolean;
  resetDelay?: number;
  onCopySuccess?: () => void;
  onCopyError?: (err: unknown) => void;
}

// ── Component ──────────────────────────────────────────────────────────────────

export const CopyToClipboard = forwardRef<
  HTMLButtonElement,
  CopyToClipboardProps
>(
  (
    {
      value,
      label,
      showLabel = false,
      size = "xs",
      resetDelay = 2000,
      onCopySuccess,
      onCopyError,
      className,
      ...rest
    },
    ref,
  ) => {
    const { t } = useTranslation("common");
    const stateMeta = useMemo(() => buildStateMeta(t), [t]);
    const { copy, state } = useCopyToClipboard({
      resetDelay,
      onSuccess: onCopySuccess,
      onError: onCopyError,
    });

    const { Icon, tooltip, label: stateLabel } = stateMeta[state];
    const resolvedSize = size ?? "xs";
    const resolvedTooltip = state === "idle" ? (label ?? tooltip) : tooltip;
    const resolvedLabel = state === "idle" ? (label ?? stateLabel) : stateLabel;

    return (
      <TooltipProvider delayDuration={400}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              ref={ref}
              type="button"
              aria-label={resolvedTooltip}
              disabled={state !== "idle"}
              onClick={(e) => {
                e.stopPropagation();
                copy(value);
              }}
              className={cn(
                copyButtonVariants({ size, copyState: state }),
                className,
              )}
              {...rest}
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={state}
                  className="flex items-center justify-center"
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.5, opacity: 0 }}
                  transition={{ duration: 0.12, ease: [0.4, 0, 0.2, 1] }}
                >
                  <Icon
                    className={ICON_SIZE[resolvedSize]}
                    strokeWidth={2.25}
                    aria-hidden
                  />
                </motion.span>
              </AnimatePresence>

              {showLabel && (
                <span
                  className={cn(
                    "leading-none font-medium tabular-nums",
                    TEXT_SIZE[resolvedSize],
                  )}
                >
                  {resolvedLabel}
                </span>
              )}

              <span className="sr-only" aria-live="polite" aria-atomic>
                {state !== "idle" ? resolvedTooltip : ""}
              </span>
            </button>
          </TooltipTrigger>

          <TooltipContent side="top" className="text-xs px-2 py-1">
            {resolvedTooltip}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  },
);

CopyToClipboard.displayName = "CopyToClipboard";
